import { createContext, useContext } from 'react';

// PRODUCTION_WORKER opens the kiosks and reaches no app data — see
// api/users.ts and the server's READ_ROLES, which deliberately omits it.
export type UserRole = 'PRODUCTION_WORKER' | 'VIEWER' | 'ENGINEER' | 'ENG_MANAGER' | 'SERVICE_MGR' | 'PURCHASER' | 'WAREHOUSE' | 'ACCOUNT_MANAGER' | 'PM' | 'VP' | 'MANAGER' | 'CEO';

export type AuthUser = {
  id: string;
  username: string;
  name: string;
  role: UserRole;
  /** התפקיד האמיתי כש-`role` הורם ל-CEO בגלל FULL_ACCESS (אלי 05/10) — לתצוגה. */
  baseRole?: UserRole;
  // The language this person chose, remembered on their record so it follows
  // them to any machine.
  preferredLanguage?: 'he' | 'ru';
  // The menu entries this person pinned to the top of the sidebar, as routes.
  // Kept on the user record rather than in the browser for the same reason the
  // language is: these people move between a desk, a floor terminal and a
  // phone, and a shortcut that only exists on one machine is a shortcut they
  // stop using. Optional — an older /me response simply has none.
  navFavorites?: string[];
  // סיורי „מה חדש" שהאדם כבר ראה או דילג עליהם (אלי 20/10/2026) — פעם אחת לכל מכשיר.
  seenTours?: string[];
  // The capabilities this user resolves to (role defaults + per-user overrides).
  capabilities: string[];
};

export type AuthState = {
  user: AuthUser | null;
  isLoading: boolean;
  error: Error | null;
  /** מחזירה '2fa' כשהסיסמה התקבלה והחשבון דורש קוד — הסשן עדיין לא פתוח. */
  login: (username: string, password: string) => Promise<'ok' | '2fa'>;
  completeTwoFactor: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  // True if the signed-in user has the given capability.
  can: (capability: string) => boolean;
};

export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be used inside <AuthProvider>');
  }
  return ctx;
}

/**
 * ── מי מחובר, **אם** מישהו מחובר ─────────────────────────────────────────
 *
 * ⚠ נמדד 21/09/2026: הוק שפת הקיוסק (`useKioskLanguage`) קרא ל-`useAuth()`
 * כדי לדעת לאיזו שפה לחזור בסוף סשן — ו-`useAuth()` **זורק** כשאין
 * `AuthProvider`. הקיוסק הוא בדיוק המשטח שבו אין אחד: כניסה בתג, בלי
 * התחברות. שש בדיקות של קיוסק הגמר וקיוסק הסריקה נפלו על
 * „useAuth() must be used inside <AuthProvider>".
 *
 * בייצור זה לא התפוצץ **במקרה** — `App.tsx` עוטף הכול ב-`AuthProvider`,
 * גם את המסלולים האנונימיים. כלומר המסך נשען על עטיפה שאין לו שום סיבה
 * להסתמך עליה, ויום שבו קיוסק ייטען מחוץ לעץ הזה הוא מסך לבן ברצפת
 * הייצור.
 *
 * ⚠ ו-`useAuth()` נשאר זורק, במכוון: מסך שדורש התחברות **חייב** להיכשל
 * ברעש אם הוא נטען בלי הקשר, ולא להציג „אין משתמש" ולהמשיך. שתי
 * הפונקציות מתארות שתי דרישות שונות, ולכן הן שתיים.
 */
export function useAuthOptional(): AuthState | null {
  return useContext(AuthContext);
}
